import { Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Divider, Stack, Theme, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from '@mui/styles';
import { format } from 'date-fns';
import useAuth from 'hooks/useAuth';
import useUser from 'hooks/useUser';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from 'react-query';
import { getErrorMessage } from 'utils/stringUtils';
import DialogClose from 'views/components/buttons/DialogClose';
import IntakeFormWizard from '../../components/intake-form-wizard';
import useIntakeForm from './useIntakeForm';

interface Props extends DialogProps {
  userId: string;
  onClose: () => void;
}

export default function IntakeFormDialog({ userId, onClose, ...props }: Props) {
  const { intakeForm, refetchIntakeForm } = useIntakeForm(userId);
  const { request } = useAuth();
  const { user, refetchUser } = useUser(userId);
  const matches = useMediaQuery('(max-width:767px)');
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [activeStepData, setActiveStepData] = useState<any>();

  const useStyles = makeStyles((theme: Theme) => ({
    intakeFormDialog: {
      '& .MuiDialog-paper': {
        width: matches && '100%',
        margin: matches && 10,
        '& .MuiStepper-horizontal': {
          overflowX: matches && 'scroll'
        }
      }
    },
    intakeFormDialogContent: {
      width: matches ? '100%' : undefined,
      '& input[type=number]': {
        '-mozAppearance': 'textfield !important'
      },
      '& .MuiFormControl-root.MuiFormControl-fullWidth': {
        '& .css-a2l1ks-MuiStack-root': {
          '& .MuiBox-root.css-1rr4qq7:nth-child(3)': {
            marginLeft: matches && 0,
            flex: matches && '0 0 45%'
          },
          '& .MuiBox-root.css-1rr4qq7:nth-child(1)': {
            lineBreak: matches && 'anywhere',
            WebkitLineBreak: matches && 'anywhere'
          }
        }
      }
    }
  }));

  const classes = useStyles();
  const updateIntakeForm = useMutation((variables: { data: any }) => request.put(`/user/${userId}/documents/intake-form`, variables.data), {
    onSuccess: () => {
      refetchIntakeForm();
    },
    onError: (e) => {
      enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
    }
  });
  const updateUserMutation = useMutation((variables: any) => request.put(`/user/${user?.id}`, variables), {
    onSuccess: () => {
      refetchUser();
    }
  });

  if (!intakeForm) {
    return null;
  }

  return (
    <Dialog
      fullWidth
      className={classes.intakeFormDialog}
      maxWidth="lg"
      sx={{ '& .MuiDialog-paper': { p: 0 } }}
      onClose={onClose}
      {...props}
    >
      <DialogTitle>
        <FormattedMessage defaultMessage="Client Intake Form" />
        <DialogClose
          onClose={() => {
            onClose();
            if (activeStep === 0) {
              if (activeStepData) {
                const { data } = activeStepData;
                updateUserMutation.mutate(data);
              }
            }
            updateIntakeForm.mutate({ data: activeStepData });
          }}
        />
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.intakeFormDialogContent} sx={{ p: 2 }}>
        <IntakeFormWizard
          userId={userId}
          onFinish={onClose}
          activeStepData={activeStepData}
          setActiveStepData={setActiveStepData}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ py: 2, px: 3 }}>
        <Stack sx={{ width: '100%' }} spacing={2}>
          <div id="intake-form-actions-wrapper" />
          <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
            <FormattedMessage defaultMessage="Last Update" /> {format(new Date(intakeForm.updatedAt), 'PPp')}
          </Typography>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
